import type { Middleware, UnknownAction } from "redux";
import { getUserLicenseInfo, setRemainingUnits } from "../actions/user";
import type { ReduxActionMeta } from "../types/ActionMeta";
import type { ISocketMiddlewareAction } from "./websocket";

const terminalResponsePeers = new Map<string, string | undefined>();

const isSocketAction = (
    action: UnknownAction
): action is ISocketMiddlewareAction<unknown> =>
    Array.isArray((action as ISocketMiddlewareAction<unknown>)?.socket?.types);

const hasStringType = (
    action: unknown
): action is UnknownAction & { type: string } =>
    !!action && typeof (action as UnknownAction).type === "string";

type ActionWithMetaAndPayload = UnknownAction & {
    meta?: ReduxActionMeta;
    payload?: unknown;
};

const hasMarkedQueryRefreshMeta = (meta?: ReduxActionMeta): boolean =>
    meta?.refreshLicenseInfoAfterCompletion === true ||
    meta?.updateUnitsFromRemainingUnits === true;

const readRemainingUnits = (payload: unknown): number | undefined => {
    if (!payload || typeof payload !== "object") {
        return undefined;
    }

    const remainingUnits = (payload as { remainingUnits?: unknown })
        .remainingUnits;
    return typeof remainingUnits === "number" ? remainingUnits : undefined;
};

const isTerminalHttpActionType = (type: string): boolean =>
    type.endsWith("_SUCCESS") ||
    type.endsWith("_ERROR") ||
    type.endsWith("_FAILURE");

export const applyQueryRefreshForPayload = (
    store: Parameters<Middleware>[0],
    payload: unknown,
    meta?: ReduxActionMeta
): void => {
    if (!hasMarkedQueryRefreshMeta(meta)) {
        return;
    }

    if (meta?.updateUnitsFromRemainingUnits) {
        const remainingUnits = readRemainingUnits(payload);
        if (remainingUnits !== undefined) {
            store.dispatch(setRemainingUnits(remainingUnits));
            return;
        }
    }

    store.dispatch(getUserLicenseInfo.action());
};

export const licenseInfoRefreshMiddleware: Middleware =
    (store) => (next) => (action: UnknownAction) => {
        if (
            isSocketAction(action) &&
            action.meta?.refreshLicenseInfoAfterCompletion
        ) {
            const [, , successType, errorType] = action.socket.types;
            if (successType) {
                terminalResponsePeers.set(successType, errorType);
            }
            if (errorType) {
                terminalResponsePeers.set(errorType, successType);
            }
        }

        const result = next(action);

        if (!hasStringType(action)) {
            return result;
        }

        const peerType = terminalResponsePeers.get(action.type);
        if (terminalResponsePeers.delete(action.type)) {
            if (peerType) {
                terminalResponsePeers.delete(peerType);
            }
            store.dispatch(getUserLicenseInfo.action());
            return result;
        }

        const actionWithMeta = action as ActionWithMetaAndPayload;
        if (
            !isSocketAction(action) &&
            isTerminalHttpActionType(action.type) &&
            hasMarkedQueryRefreshMeta(actionWithMeta.meta)
        ) {
            applyQueryRefreshForPayload(
                store,
                actionWithMeta.payload,
                actionWithMeta.meta
            );
        }

        return result;
    };
